const INTRO_SESSION_COOKIE = "figueFvIntroSeen";
const INTRO_FALLBACK_TIMEOUT = 60000;
const INTRO_TIMEOUT_PADDING = 5000;
const FORCE_INTRO_PREVIEW = false;
const WHITEOUT_DURATION = 480;
const HEADER_REVEAL_DELAY = 1680;
const reducedMotionQuery = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
);

const getVideoSource = (video) => {
  const probe = document.createElement("video");
  const supportsHevc = probe.canPlayType('video/quicktime; codecs="hvc1"');

  return supportsHevc ? video.dataset.mov : video.dataset.webm;
};

const setVideoSource = (video) => {
  if (!video.src) {
    video.src = getVideoSource(video);
    video.load();
  }
};

const hasSeenIntro = () => {
  const cookiePrefix = `${INTRO_SESSION_COOKIE}=`;

  return document.cookie
    .split(";")
    .some((cookie) => cookie.trim().startsWith(cookiePrefix));
};

const rememberIntro = () => {
  document.cookie = `${INTRO_SESSION_COOKIE}=true; path=/; SameSite=Lax`;
};

const isAnchorNavigation = () => window.location.hash.length > 1;

const playVideo = async (video) => {
  video.muted = true;
  video.defaultMuted = true;
  await video.play();
};

const wait = (duration) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, duration);
  });

export const initTopHero = () => {
  const hero = document.querySelector(".js-top-hero");

  if (!hero) return;

  const mainVideo = hero.querySelector(".js-top-hero-main");
  const loopVideo = hero.querySelector(".js-top-hero-loop");

  if (!mainVideo || !loopVideo) return;

  let isUnlocked = false;
  let isSwitching = false;
  let timeoutId;

  const unlockPage = () => {
    if (isUnlocked) return;

    isUnlocked = true;
    window.clearTimeout(timeoutId);
    document.documentElement.classList.remove("is-fv-intro-pending");
    hero.classList.add("is-ready");
  };

  const activateLoopLayout = () => {
    hero.classList.add("is-loop-layout");
    loopVideo.classList.add("is-current");
    mainVideo.classList.remove("is-current");
  };

  const showLoop = async ({
    autoplay = true,
    transition = false,
    immediate = false,
  } = {}) => {
    if (isSwitching) return;

    isSwitching = true;
    setVideoSource(loopVideo);

    if (immediate) {
      activateLoopLayout();
      unlockPage();
    }

    if (autoplay) {
      try {
        await playVideo(loopVideo);
      } catch {
        loopVideo.currentTime = 0;
      }
    }

    if (transition) {
      hero.classList.add("is-whiteout");
      await wait(WHITEOUT_DURATION);
    }

    activateLoopLayout();

    if (transition) {
      hero.classList.remove("is-whiteout");
      await wait(HEADER_REVEAL_DELAY);
    }

    unlockPage();
  };

  const showIntro = async () => {
    hero.classList.add("is-intro");
    mainVideo.classList.add("is-current");
    setVideoSource(mainVideo);
    rememberIntro();

    const setIntroFallback = (duration = INTRO_FALLBACK_TIMEOUT) => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        showLoop({ transition: true });
      }, duration);
    };

    setIntroFallback();

    mainVideo.addEventListener(
      "loadedmetadata",
      () => {
        if (Number.isFinite(mainVideo.duration)) {
          setIntroFallback(mainVideo.duration * 1000 + INTRO_TIMEOUT_PADDING);
        }
      },
      { once: true },
    );

    mainVideo.addEventListener(
      "playing",
      () => {
        setVideoSource(loopVideo);
      },
      { once: true },
    );
    mainVideo.addEventListener(
      "ended",
      () => {
        showLoop({ transition: true });
      },
      { once: true },
    );
    mainVideo.addEventListener(
      "error",
      () => {
        showLoop({ transition: true });
      },
      { once: true },
    );

    try {
      await playVideo(mainVideo);
    } catch {
      showLoop({ transition: true });
    }
  };

  if (reducedMotionQuery.matches) {
    rememberIntro();
    showLoop({ autoplay: false });
    return;
  }

  if (!FORCE_INTRO_PREVIEW && isAnchorNavigation()) {
    rememberIntro();
    showLoop({ immediate: true });
    return;
  }

  if (!FORCE_INTRO_PREVIEW && hasSeenIntro()) {
    showLoop();
    return;
  }

  showIntro();
};
